
import React from "react";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CartItem } from "@/types/apparelTypes";
import { formatCurrency } from "@/lib/format";

interface CartItemProps {
  item: CartItem;
  index: number;
  onRemove: (index: number) => void;
  onUpdateQuantity: (index: number, quantity: number) => void;
  onUpdateSize?: (index: number, size: string) => void;
}

export const CartItemComponent: React.FC<CartItemProps> = ({
  item,
  index,
  onRemove,
  onUpdateQuantity,
  onUpdateSize,
}) => {
  const { product, quantity, size } = item;
  
  const itemPrice = product.price_cents * quantity;
  
  const handleQuantityAdjust = (amount: number) => {
    const newQuantity = quantity + amount;
    if (newQuantity > 0) {
      onUpdateQuantity(index, newQuantity);
    }
  };
  
  const handleSizeChange = (newSize: string) => {
    if (onUpdateSize) {
      onUpdateSize(index, newSize);
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="flex gap-4 py-4 border-b border-gray-100 last:border-0"
    >
      <div className="h-20 w-20 rounded overflow-hidden bg-gray-100 flex-shrink-0">
        {product.image_url ? (
          <img 
            src={product.image_url} 
            alt={product.name} 
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400 text-xs">
            No image
          </div>
        )}
      </div>
      
      <div className="flex flex-col flex-1">
        <div className="flex justify-between">
          <h4 className="font-medium text-gray-900">{product.name}</h4>
          <Button 
            variant="destructive"
            size="icon"
            onClick={() => onRemove(index)}
            className="h-8 w-8"
            aria-label="Remove item"
          >
            <Trash2 size={16} />
          </Button>
        </div>
        
        <div className="text-sm text-gray-500 mt-1 mb-2">{formatCurrency(product.price_cents / 100)}</div>
        
        {product.requires_size && (
          <div className="mb-3">
            <Select
              value={size}
              onValueChange={handleSizeChange}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select a size" />
              </SelectTrigger>
              <SelectContent>
                {Array.isArray(product.sizes) ? (
                  product.sizes.map((sizeOption) => (
                    <SelectItem key={sizeOption} value={sizeOption}>
                      {sizeOption}
                    </SelectItem>
                  ))
                ) : product.sizes ? (
                  Object.keys(product.sizes).map((sizeOption) => (
                    <SelectItem key={sizeOption} value={sizeOption}>
                      {sizeOption}
                    </SelectItem>
                  ))
                ) : null}
              </SelectContent>
            </Select>
          </div>
        )}
        
        <div className="flex items-center justify-between mt-auto">
          <div className="flex items-center border rounded-md">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleQuantityAdjust(-1)}
              className="px-2 h-8"
              disabled={quantity <= 1}
            >
              <Minus size={14} />
            </Button>
            <span className="px-2 w-8 text-center">{quantity}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleQuantityAdjust(1)}
              className="px-2 h-8"
            >
              <Plus size={14} />
            </Button>
          </div>
          
          <div className="font-medium">
            {formatCurrency(itemPrice / 100)}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
